import {DescriptionRounded, FileUploadRounded, SendRounded,} from "@mui/icons-material";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import AdvancedFilter from "../../component/AdvancedFilter";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import * as Yup from 'yup';
import {Box, Button, Checkbox, Chip, Grid, IconButton, ListItemText, MenuItem, TextField} from "@mui/material";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {ColDef, GetRowIdParams, GridOptions, ICellRendererParams} from "ag-grid-enterprise";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import dayjs from "dayjs";
import {IsRowSelectable, RowNode, SelectionChangedEvent} from "ag-grid-community";
import {useFinesStatusValues} from "../../component/useFinesStatusValues";
import {FinesStatusInterface} from "../../component/FinesInterfaces";
import {useFinesActions} from "../../service/useFinesActions";
import {DatePicker} from "@mui/x-date-pickers";
import CustomModalBL from "../../component/CustomModalBL";
import FileUploader from "../../../../util/component/FileUploader";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import EmptyState from "../../../../util/component/EmptyState";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CustomModalContent from "../../component/CustomModalContent";
import {DECIMAL_REG_EXP} from "../../component/Constants";
import {FINES_SCOPE_TO_INVOICE, FINES_SCOPE_UPLOAD, FINES_SCOPE_VIEW} from "../../component/utils/AuthConstants";
import {renderMultiSelect} from "../../common/FormSelect";
import FormSelect from "../../../finance/experiments/forms/FormSelect";
import {useForm} from "react-hook-form";

const FinesList = (props: any) => {
    const {t} = useTranslation(['finessecurity'], {keyPrefix: 'fines'});
    const {t: tr} = useTranslation("translation");
    const hasScope = useHasScope;
    const {
        getFinesList, getFineStatus, sendToInvoice, importFile
    } = useFinesActions();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const agGridLocale = useAgGridLocale();
    const goToInvoiceDetail = '/finessecurity/fines/detail/';
    const gridRef = useRef<AgGridReact>(null);
    const [uploadErrorMessage, setUploadErrorMessage] = useState<string>("");
    const [openUploadWarning, setOpenUploadWarning] = useState(false);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [openSendInInvoicing, setOpenSendInInvoicing] = useState(false);
    const [fileForImport, setFileForImport] = useState<File | undefined>(undefined);
    const [finesListTableShouldUpdate, setFinesListTableShouldUpdate] = useState(false);
    const {getFinesStatusValues, getFinesStatusColor} = useFinesStatusValues();
    const [fineStatus, setFineStatus] = useState<FinesStatusInterface[]>([]);
    const [selectedItemsInternal, setSelectedItemsInternal] = useState<any | undefined>();
    const [items, setItems] = useState<any | undefined>([]);
    const finesType = props.finesType;

    const legalEntityOptions: any[] = globalData?.user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tr(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = globalData?.user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tr(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = React.useState<any[]>(dealerOptions);
    const filterDefaultValues = {
        reportNumber: "", plate: "", raNumber: "", driver: "",
        fineDateFrom: null, fineDateTo: null, invoiceDateFrom: null, invoiceDateTo: null,
        fineAmountFrom: "", fineAmountTo: "", penaltyAmountFrom: "", penaltyAmountTo: "",
        fineStatus: [1, 2],
        legalEntities: legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };
    const [filters, setFilters] = useState<any>(filterDefaultValues);
    const [doSearchFlag, setDoSearchFlag] = useState<boolean>(false);

    const validationObj = {
        reportNumber: Yup.string().nullable(),
        plate: Yup.string().nullable(),
        raNumber: Yup.string().nullable(),
        driver: Yup.string().nullable(),
        fineDateFrom: Yup.date().nullable()
            .test("testFineDateFrom", t('errors.datesCheck'), (value, context) => {
                return (!value || !context.parent.fineDateTo || value <= context.parent.fineDateTo)
            })
            .typeError(t('errors.invalidDate')),
        fineDateTo: Yup.date().nullable()
            .test("testFineDateTo", t('errors.datesCheck'), (value, context) => {
                return (!value || !context.parent.fineDateFrom || value >= context.parent.fineDateFrom)
            })
            .typeError(t('errors.invalidDate')),
        invoiceDateFrom: Yup.date().nullable()
            .test("testInvoiceDateFrom", t('errors.datesCheck'), (value, context) => {
                return (!value || !context.parent.invoiceDateTo || value <= context.parent.invoiceDateTo)
            })
            .typeError(t('errors.invalidDate')),
        invoiceDateTo: Yup.date().nullable()
            .test("testInvoiceDateTo", t('errors.datesCheck'), (value, context) => {
                return (!value || !context.parent.invoiceDateFrom || value >= context.parent.invoiceDateFrom)
            })
            .typeError(t('errors.invalidDate')),
        fineAmountFrom: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('updateForm.onlyDecimalNumbers')
        }).test("testFineAmountFrom", t('errors.amountCheck'), (value, context) => {
            return (!value || !context.parent.fineAmountTo || value <= context.parent.fineAmountTo)
        }),
        fineAmountTo: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('errors.onlyDecimalNumbers')
        }).test("testFineAmountTo", t('errors.amountCheck'), (value, context) => {
            return (!value || !context.parent.fineAmountFrom || value >= context.parent.fineAmountFrom)
        }),
        penaltyAmountFrom: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('updateForm.onlyDecimalNumbers')
        }).test("testPenaltyAmountFrom", t('errors.amountCheck'), (value, context) => {
            return (!value || !context.parent.penaltyAmountTo || value <= context.parent.penaltyAmountTo)
        }),
        penaltyAmountTo: Yup.string().matches(DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('updateForm.onlyDecimalNumbers')
        }).test("testPenaltyAmountTo", t('errors.amountCheck'), (value, context) => {
            return (!value || !context.parent.penaltyAmountFrom || value >= context.parent.penaltyAmountFrom)
        }),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tr('validation.selectAtLeastOne'))
    }

    useEffect(() => {
        getFineStatus().then((res: any) => {
            setFineStatus(res.result);
        });
    }, []);

    const renderVerbalNumber = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.reportNumber')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderPlate = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.plate')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderRaNumber = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.raNumber')}
            value={field.value}
            onChange={field.onChange}
        />
    )
    const renderDriver = (field: any) => (
        <TextField
            fullWidth
            label={t('columns.driverName')}
            value={field.value}
            onChange={field.onChange}
        />
    )

    const renderFineDateFrom = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.fineDateFrom')}
            value={field.value}
            onChange={field.onChange}
            maxDate={values["fineDateTo"] ? values["fineDateTo"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"fineDateFrom"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)
    const renderFineDateTo = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.fineDateTo')}
            value={field.value}
            onChange={field.onChange}
            minDate={values["fineDateFrom"] ? values["fineDateFrom"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"fineDateTo"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)
    const renderInvoiceDateFrom = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.invoiceDateFrom')}
            value={field.value}
            onChange={field.onChange}
            maxDate={values["invoiceDateTo"] ? values["invoiceDateTo"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"invoiceDateFrom"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)
    const renderInvoiceDateTo = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.invoiceDateTo')}
            value={field.value}
            onChange={field.onChange}
            minDate={values["invoiceDateFrom"] ? values["invoiceDateFrom"] : ""}
            renderInput={(params) =>
                <TextField {...params} fullWidth
                           id={"invoiceDateTo"}
                           error={error !== undefined}
                           helperText={error ? error.message : ""}
                />} inputFormat={"L"}
        />)

    const renderfineStatus = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.status')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => {
                    let result = fineStatus.length > 0 ? selected.map((l: any) =>
                        getFinesStatusValues(l)).join(", ") : "";
                    return result;
                },
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {fineStatus &&
                fineStatus?.map((l) => (
                    <MenuItem key={l.fineStatusCode} value={l.fineStatusCode}>
                        <Checkbox color={"accent"} checked={field.value.indexOf(l.fineStatusCode) > -1}/>
                        <ListItemText primary={getFinesStatusValues(l.fineStatusCode)}/>
                    </MenuItem>
                ))}
        </TextField>
    )
    const renderFineAmountFrom = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            label={t('filters.fineAmountFrom')}
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    )
    const renderFineAmountTo = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            label={t('filters.fineAmountTo')}
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    )
    const renderPenaltyAmountFrom = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            label={t('filters.penaltyAmountFrom')}
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    )
    const renderPenaltyAmountTo = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            label={t('filters.penaltyAmountTo')}
            value={field.value}
            onChange={field.onChange}
            error={error !== undefined}
            helperText={error ? error.message : ""}
        />
    )

    const mainFields = {
        reportNumber: renderVerbalNumber,
        plate: renderPlate,
        fineStatus: renderfineStatus,
    }

    const fields = {
        fineDateFrom: renderFineDateFrom,
        fineDateTo: renderFineDateTo,
        invoiceDateFrom: renderInvoiceDateFrom,
        invoiceDateTo: renderInvoiceDateTo,
        fineAmountFrom: renderFineAmountFrom,
        fineAmountTo: renderFineAmountTo,
        penaltyAmountFrom: renderPenaltyAmountFrom,
        penaltyAmountTo: renderPenaltyAmountTo,
        driver: renderDriver,
        raNumber: renderRaNumber,
        legalEntities: renderMultiSelect(
            tr("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
                setValue('dealers', []);
                let dealers = globalData?.user?.dealers
                    ?.filter(
                        (x: any) =>
                            value?.length > 0 &&
                            x.legalEntities.some((e: any) => value.includes(e.name)) &&
                            x.name.toLowerCase() !== "all"
                    )
                    .map((x: any) => {
                        return {
                            label: tr(`dealers.${x.name}`),
                            value: x.name,
                        };
                    });
                setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tr("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    }

    const toolbar = {
        renderActions: () =>
            <>
                {hasScope(FINES_SCOPE_UPLOAD) &&
                    <Button variant={"contained"} size="small"
                            onClick={() => {
                                setOpenUploadModal(true);
                            }}
                            component="label" startIcon={<FileUploadRounded/>}>
                        {t('buttons.importFromFile')}
                    </Button>
                }
                {hasScope(FINES_SCOPE_TO_INVOICE) &&
                    <Button size="small" variant={"contained"}
                            disabled={!(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length == 0)}
                            onClick={() => {
                                setOpenSendInInvoicing(true);
                            }}
                            startIcon={<SendRounded/>}
                    >
                        {t('buttons.sendToInvoice')}
                    </Button>
                }
            </>
    }

    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (params: RowNode<any>) => {
            return !!params.data && params.data.fineStatus.fineStatusCode === 1;
        };
    }, []);

    const columns = useMemo(() => {
        const columns: ColDef[] = [
            {
                field: "reportNumber",
                headerName: t('columns.reportNumber'),
                unSortIcon: true,
                checkboxSelection: true,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                lockVisible: true, lockPinned: true,
                showDisabledCheckboxes: true,
                tooltipValueGetter: (params: any) => {
                    return params.data.reportNumber
                },
            },
            {
                field: "fineDatetime",
                headerName: t('columns.fineDatetime'),
                cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
                unSortIcon: true,
            },
            {
                field: "plate",
                headerName: t('columns.plate'),
                unSortIcon: true,
                cellRenderer: (params: any) => (params.value ? params.value : ""),
                tooltipValueGetter: (params: any) => (params.value),
            },
            {
                field: "fineAmount",
                headerName: t('columns.fineAmount'),
                unSortIcon: true,
                cellRenderer: (params: any) => (params.value ? params.value : ""),
                tooltipValueGetter: (params: any) => (params.value ? params.value : "")
            },
            {
                field: "penalityAmount",
                headerName: t('columns.penaltyAmount'),
                unSortIcon: true,
                cellRenderer: (params: any) => (params.value ? params.value : ""),
                tooltipValueGetter: (params: any) => (params.value ? params.value : "")
            },
            {
                field: "invoiceDatetime",
                headerName: t('columns.invoiceDate'),
                unSortIcon: true,
                cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
                tooltipValueGetter: (params: any) => (params.value ? params.value : "")
            },
            {
                field: "driverName",
                headerName: t('columns.driverName'),
                unSortIcon: true,
                cellRenderer: (params: any) => (params.value ? params.value : ""),
                tooltipValueGetter: (params: any) => (params.value ? params.value : "")
            },
            {
                field: "ra",
                headerName: t('columns.ra'),
                unSortIcon: true,
                cellRenderer: (params: any) => (params.value ? params.value : ""),
                tooltipValueGetter: (params: any) => (params.value ? params.value : "")
            },
            {
                field: "fineStatus",
                headerName: t('columns.status'),
                sortable: false,
                cellRenderer: (params: any) => {
                    return <Chip label={getFinesStatusValues(params.value.fineStatusCode)}
                                 sx={{backgroundColor: getFinesStatusColor(params.value.fineStatusCode)}}/>
                }
            },
            {
                field: "tenant",
                headerName: t('columns.tenant'),
                unSortIcon: true
            },
            {
                field: "dealer",
                headerName: t('columns.dealer'),
                unSortIcon: true
            },
            {
                field: "_actions",
                headerName: t('columns.actions'),
                pinned: 'right',
                lockPosition: 'right',
                lockPinned: true,
                sortable: false,
                width: 100,
                hide: !hasScope(FINES_SCOPE_VIEW),
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <IconButton color={"accent"}
                                        onClick={() => {
                                            window.open(goToInvoiceDetail + params.data.id, "_blank");
                                        }}>
                                <DescriptionRounded/>
                            </IconButton>
                        </Box>
                    )
                }
            }
        ];
        return columns;
    }, [])

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        pagination: true,  // Abilita la paginazione client-side
        paginationPageSize: 10,  // Imposta il numero di righe per pagina
        localeText: agGridLocale,
    };
    const onSelectionChanged = (event: SelectionChangedEvent) => {
        let selectedRows = gridRef.current!.api.getSelectedRows();
        setSelectedItemsInternal(selectedRows.map(s => s.id));
    };

    useEffect(() => {
        setSelectedItemsInternal(props.selectedItems)
    }, [props.selectedItems]);

    function retrieveFineList() {
        getFinesList(filters, finesType).then((res: any) => {
            setItems(res.content);
            setDoSearchFlag(false);
        })
    }

    const handleSetFilters = (data: any) => {
        setFilters(data);
        setDoSearchFlag(true);
    }

    useEffect(() => {
        if (!!gridRef.current?.api) {
            gridRef.current?.api?.hideOverlay()
        }
    }, [gridRef.current?.api]);

    useEffect(() => {
        if (doSearchFlag) {
            retrieveFineList();
        }
    }, [doSearchFlag]);
    useEffect(() => {
        retrieveFineList();
    }, []);

    useEffect(() => {
        if (finesListTableShouldUpdate) {
            setFinesListTableShouldUpdate(false);
        }
    }, [finesListTableShouldUpdate]);

    const uploadFinesReportFile = (fileForImport: File) => {

        let fileExtension = fileForImport.name.split('.').pop();
        let legalEntity = watch('legalEntity');
        let dealer = watch('dealer');
        if (legalEntityOptions.length <= 1) {
            legalEntity = legalEntityOptions[0].value;
            dealer = dealerOptions[0].value;
        }

        if (fileExtension!.toLowerCase() === "xlsx") {
            importFile(fileForImport, fileForImport.name.split('.')[0], legalEntity, dealer, finesType)
                .then(response => {
                    if (response.status === "OK") {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t("messages.successImport"),
                                    type: snackbarTypes.success,
                                    show: true,
                                    autoClose: true,
                                }
                            }
                        });
                        setFinesListTableShouldUpdate(true);
                        setOpenUploadModal(false);
                    } else {
                        if (response.message) {
                            let handleMessageError = response.message.split("|");
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message:
                                            t(`importErrors.${handleMessageError[0]}`,
                                                response.code === -50 ?
                                                    {
                                                        line: handleMessageError[1]
                                                    } :
                                                    {
                                                        field: handleMessageError[1].toUpperCase(),
                                                        line: handleMessageError[2]
                                                    }
                                            ),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true,
                                    }
                                }
                            });
                        }
                    }
                })
                .finally(() => {
                    setFileForImport(undefined);
                });
        } else {
            setUploadErrorMessage(t('importErrors.wrongFileFormatCSV'));
            setOpenUploadWarning(true);
            setFileForImport(undefined);
        }

    }

    const sendReportsToInvoice = (ids: any) => {
        sendToInvoice({ids: ids}).then(response => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("messages.sendInvoiceSuccess"),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            setFinesListTableShouldUpdate(true);
        }).catch(
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("errors.sendInvoiceError"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
        ).finally(() => {
                setOpenSendInInvoicing(false);
            }
        )

    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            title: t("noReports"),
            content: t("noReportsDescription")
        };
    }, []);

    const reset = () => {
        setFilters(filterDefaultValues);
    };

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = globalData?.user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tr(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }
    const {watch, control} = useForm<any>({});

    return (
        <>
            <Box>
                <AdvancedFilter validationObj={validationObj} fields={fields} defaultValues={filterDefaultValues}
                                mainFields={mainFields} filters={filters} onReset={reset}
                                setFilters={handleSetFilters}/>

                <Grid container>
                    <Grid item xs={12} sx={{padding: "16px"}}>
                        <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef}
                            // pagination={false}
                                           rowSelection={'multiple'}
                                           rowData={items}
                                           onSelectionChanged={onSelectionChanged}
                                           toolbar={toolbar}
                                           isRowSelectable={isRowSelectable}
                                           noRowsOverlayComponent={noRowsOverlayComponent}
                                           noRowsOverlayComponentParams={noRowsOverlayComponentParams}/>
                    </Grid>
                </Grid>
            </Box>
            <CustomModalContent
                open={openUploadModal}
                onClose={() => {
                    setOpenUploadModal(false);
                    setUploadErrorMessage(t('importErrors.wrongFileFormat'));
                    setDealers([]);
                }}
                cancelButton={t('buttons.close')}
                title={t('buttons.importFromFile')}
                content={
                    <>
                        {legalEntityOptions.length > 1 &&
                            <>
                                <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                                    <Grid item xs={6}>
                                        <FormSelect
                                            name={"legalEntity"}
                                            control={control}
                                            props={{
                                                label: tr("shared.legalEntities"),
                                                fullWidth: true,
                                                required: true,
                                                onChange: (e: any) => {
                                                    handleChangeLegalEntities(e.target.value)
                                                },
                                            }}
                                            items={legalEntityOptions.map((option: any) => (
                                                <MenuItem key={option.value}
                                                          value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        />
                                    </Grid>

                                    <Grid item md={6} sm={3} xs={12}>
                                        <FormSelect
                                            name={"dealer"}
                                            control={control}
                                            props={{
                                                label: tr("shared.dealers"),
                                                fullWidth: true,
                                                required: true,
                                                disabled: dealers.length == 0
                                            }}
                                            items={dealers.map((option: any) => (
                                                <MenuItem key={option.value}
                                                          value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        }
                        <FileUploader
                            errorMessage={uploadErrorMessage}
                            label={t('buttons.selectFile')}
                            onUpload={(file: File) => {
                                uploadFinesReportFile(file);
                            }}
                            acceptedFiles={{'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']}}
                            disabled={watch("legalEntity") === "" || watch("dealer") === ""}
                        />

                    </>
                }
            />
            <CustomModalBL
                open={openSendInInvoicing} title={t('sendingModal.sendConfirmation')}
                content={t('sendingModal.sendConfirmationMsg', {n: selectedItemsInternal ? selectedItemsInternal.length : 0})}
                cancelButton={t('buttons.cancel')}
                saveButton={t('buttons.confirmSending')}
                titleIcon={<InfoRoundedIcon/>}
                onSave={() => {
                    setOpenSendInInvoicing(false);
                    sendReportsToInvoice(selectedItemsInternal);
                }}
                onClose={() => {
                    setOpenSendInInvoicing(false);
                }}/>
        </>

    )
}

export default FinesList;
